import * as React from 'react';
import styled, { css } from 'styled-components';
import {
  Thumbnail,
  Link,
  SkeletonInput,
  theme,
  ParsedDetailsResult,
} from 'components';
import { IClientContact } from '@bb/contacts';

import { buildListingFullAddress } from '../../../../../utils';

export interface AddressListingCellProps {
  address?: ParsedDetailsResult | string;
  listings?: IClientContact['listings'];
  loading?: boolean;
}

const thumbnailStyle = css`
  width: 28px;
  height: 28px;
  margin-right: 2rem;
  flex-shrink: 0;
`;

const AddressListingCell: React.FC<AddressListingCellProps> = ({
  address,
  listings = [],
  loading,
}) => {
  if (loading) {
    return (
      <>
        <Thumbnail wrapperStyle={thumbnailStyle} loading />
        <StyledSkeletonInput size="small" />
      </>
    );
  }

  const [listing] = listings;

  if (listing) {
    const { address: listingAddress } = listing;
    return (
      <>
        <Thumbnail wrapperStyle={thumbnailStyle} src={listing.thumbnail} />
        <StyledLink
          onClick={e => e.stopPropagation()}
          href={`/#/listing/${listing.id}/view`}
        >
          {`${buildListingFullAddress(
            listingAddress.line1,
            listingAddress.line2,
          )}, ${listingAddress.city}, ${listingAddress.province}, ${
            listingAddress.postalCode
          }`}
        </StyledLink>
        {listings.length > 1 && (
          <StyledNote>{`+ ${listings.length - 1} more`}</StyledNote>
        )}
      </>
    );
  }

  if (address) {
    let _address = address;

    if (typeof address === 'object') {
      _address = `${buildListingFullAddress(address.line1, address.line2)}, ${
        address.city
      }, ${address.province}, ${address.postalCode}`;
    }

    return (
      <>
        <Thumbnail wrapperStyle={thumbnailStyle} />
        {_address}
      </>
    );
  }

  return null;
};

export default React.memo(AddressListingCell);

const StyledSkeletonInput = styled(SkeletonInput)`
  width: 100%;
`;

const StyledNote = styled.span`
  font-size: 14px;
  line-height: 22px;
  color: ${theme.gray6};
  padding-left: 10px;
  white-space: nowrap;
`;

const StyledLink = styled(Link)`
  &&& {
    color: ${theme.black};
    &:hover {
      text-decoration: underline;
    }
  }
  display: inline-block;
  flex: 0 1 auto;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;
